/*
  Page lock for the ticket app's checkout modal.

  The app (Evey) renders and opens the modal itself, so there is no event to
  listen to. html.evey-modal-open — which theme-evey-modal.css hides the page's
  overflow on — follows the modal's visibility instead, and the wheel then
  scrolls the modal, which has its own scrollport.

  Polled rather than observed: the app replaces its container while opening, so
  a MutationObserver attached to that container stopped receiving anything after
  the first render and the lock never lifted. One querySelector three times a
  second costs nothing and cannot go stale.

  Lenis is the other half. It scrolls the page from raw wheel events, so
  overflow: hidden alone does not stop it — measured: the page still moved 500px
  underneath an open modal. It is stopped for as long as the modal is up, and
  re-checked every tick, because the cart drawer starts it again as it closes on
  the way to checkout.

  It also repairs the links in the app's own copy. The privacy notice in the
  data-controller paragraph ships as href="&quot;https://…&quot;", i.e. the
  quotes are part of the value, which makes the browser read it as a relative
  path and the link goes nowhere. The quotes are trimmed off and outbound links
  are sent to a new tab, so the form being filled in is not lost.

  And it moves the required marker. The app prints the asterisk as a bare text
  node after the label — "First name*" — where it reads as part of the wording.
  It is lifted into its own span at the front of the label, which the stylesheet
  then colours; a real element rather than a ::before, so it stays in the text a
  screen reader announces.

  Links in translations: the app's strings are edited as JSON, which will not
  take HTML, so a link is written in Markdown instead —

      Read the [Privacy Notice](https://example.com/privacy) before you continue.

  and turned into a real anchor here. The anchor is built element by element,
  never through innerHTML, and the pattern only matches http(s), so a string
  coming from the app can add a link but cannot inject markup or a javascript:
  URL.
*/
(() => {
  const MODAL = '.evey-modal';
  let open = false;

  const visibleModal = () => {
    const modal = document.querySelector(MODAL);
    if (!modal) return null;
    const visible =
      getComputedStyle(modal).display !== 'none' && modal.getBoundingClientRect().height > 0;
    return visible ? modal : null;
  };

  const tidyLinks = (modal) => {
    modal.querySelectorAll('a[href]').forEach((link) => {
      if (link.dataset.eveyLinkTidied === 'true') return;
      link.dataset.eveyLinkTidied = 'true';

      const raw = link.getAttribute('href') || '';
      const href = raw.trim().replace(/^["'\s]+/, '').replace(/["'\s]+$/, '');
      // Only ever unwrap what is already there — never rewrite a scheme.
      if (href && href !== raw && !/^javascript:/i.test(href)) link.setAttribute('href', href);

      if (/^https?:\/\//i.test(href)) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
  };

  const markRequired = (modal) => {
    modal.querySelectorAll('.attendee-info-label').forEach((label) => {
      if (label.dataset.eveyRequired) return;

      // The asterisk is a text node of its own, sitting after the label's span.
      const star = Array.prototype.find.call(
        label.childNodes,
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.includes('*')
      );
      label.dataset.eveyRequired = star ? 'true' : 'false';
      if (!star) return;

      star.textContent = star.textContent.replace(/\*/g, '');
      const marker = document.createElement('span');
      marker.className = 'evey-required';
      marker.textContent = '*';
      label.insertBefore(marker, label.firstChild);
    });
  };

  // [label](https://url) — the only markup the JSON translations can carry.
  const MARKDOWN_LINK = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g;

  const linkifyText = (modal) => {
    const walker = document.createTreeWalker(modal, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        // Cheap reject first: the walker runs over the whole modal every tick.
        if (!node.textContent.includes('](')) return NodeFilter.FILTER_REJECT;
        if (node.parentElement && node.parentElement.closest('a, script, style, textarea')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      const text = node.textContent;
      MARKDOWN_LINK.lastIndex = 0;
      if (!MARKDOWN_LINK.test(text)) return;

      MARKDOWN_LINK.lastIndex = 0;
      const fragment = document.createDocumentFragment();
      let index = 0;
      let match = MARKDOWN_LINK.exec(text);

      while (match) {
        fragment.append(text.slice(index, match.index));

        const link = document.createElement('a');
        link.href = match[2];
        link.textContent = match[1];
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        // Built correctly already; nothing for tidyLinks to unwrap.
        link.dataset.eveyLinkTidied = 'true';
        fragment.append(link);

        index = match.index + match[0].length;
        match = MARKDOWN_LINK.exec(text);
      }

      fragment.append(text.slice(index));
      node.parentNode.replaceChild(fragment, node);
    });
  };

  const sync = () => {
    const modal = visibleModal();
    const isOpen = Boolean(modal);

    // Re-asserted every tick: the cart drawer releases the lock as it closes on
    // the way to checkout, which is exactly when this modal opens.
    if (modal) {
      modal.setAttribute('data-lenis-prevent', '');
      // Re-run every tick: the app re-renders the form, and fresh links arrive
      // without the flag this leaves behind.
      markRequired(modal);
      linkifyText(modal);
      tidyLinks(modal);
      if (!window.ScrollLock?.locked) window.ScrollLock?.lock(modal);
      if (window.lenis && !window.lenis.isStopped) window.lenis.stop();
    }

    if (isOpen === open) return;
    open = isOpen;
    document.documentElement.classList.toggle('evey-modal-open', open);
    if (!open) window.ScrollLock?.unlock();
  };

  setInterval(sync, 300);
  // Checkout is a click, so the lock lands on the same frame the app opens on.
  document.addEventListener('click', () => setTimeout(sync, 0), true);
  sync();
})();
